import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { StatusBadge, LoadingSpinner } from '../../components/UI';
import { format } from 'date-fns';

const STATUS_STEP = {
  submitted:          1,
  under_review:       1,
  objection_by_related: 1,
  approved_by_related: 2,
  objection_by_final: 2,
  approved_final:     3,
  rejected_final:     3,
  rejected_by_related: 1,
  pulled:             0,
};

const STATUS_COLOR = {
  submitted:           'bg-yellow-50 border-yellow-300 text-yellow-700',
  under_review:        'bg-blue-50 border-blue-300 text-blue-700',
  approved_by_related: 'bg-indigo-50 border-indigo-300 text-indigo-700',
  approved_final:      'bg-green-50 border-green-300 text-green-700',
  rejected_by_related: 'bg-red-50 border-red-300 text-red-700',
  rejected_final:      'bg-red-50 border-red-300 text-red-700',
  objection_by_related:'bg-orange-50 border-orange-300 text-orange-700',
  objection_by_final:  'bg-orange-50 border-orange-300 text-orange-700',
  pulled:              'bg-gray-50 border-gray-300 text-gray-500',
};

export default function FlowTracking() {
  const [apps, setApps]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filters, setFilters]   = useState({ status: '', noc_type_id: '', date_from: '', date_to: '' });
  const [nocTypes, setNocTypes] = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const LIMIT = 20;

  useEffect(() => {
    api.get('/masters/noc-types').then(r => setNocTypes(r.noc_types || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [filters, page]);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({
      limit: LIMIT,
      page,
      ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
    });
    api.get(`/admin/applications?${params}`)
      .then(r => { setApps(r.applications || []); setTotal(r.total || 0); })
      .finally(() => setLoading(false));
  };

  const filtered = apps.filter(a =>
    !search ||
    a.application_no?.toLowerCase().includes(search.toLowerCase()) ||
    a.user_name?.toLowerCase().includes(search.toLowerCase()) ||
    a.noc_name?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">🔀 Flow Tracking</h1>
        <p className="text-sm text-gray-500 mt-1">Track application journey through departments</p>
      </div>

      {/* Filters */}
      <div className="card mb-4 py-3">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <input className="form-input text-sm col-span-2 lg:col-span-1"
            placeholder="Search app no / name..."
            value={search} onChange={e => setSearch(e.target.value)} />
          <select className="form-select text-sm" value={filters.status}
            onChange={e => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}>
            <option value="">All Status</option>
            <option value="submitted">Submitted</option>
            <option value="under_review">Under Review</option>
            <option value="approved_by_related">Forwarded</option>
            <option value="approved_final">Final Approved</option>
            <option value="rejected_final">Rejected</option>
            <option value="objection_by_related">Objection (Related)</option>
            <option value="objection_by_final">Objection (Final)</option>
            <option value="pulled">Pulled</option>
          </select>
          <select className="form-select text-sm" value={filters.noc_type_id}
            onChange={e => { setFilters(f => ({ ...f, noc_type_id: e.target.value })); setPage(1); }}>
            <option value="">All NOC Types</option>
            {nocTypes.map(n => <option key={n.id} value={n.id}>{n.noc_name}</option>)}
          </select>
          <input type="date" className="form-input text-sm" value={filters.date_from}
            onChange={e => { setFilters(f => ({ ...f, date_from: e.target.value })); setPage(1); }} />
          <input type="date" className="form-input text-sm" value={filters.date_to}
            onChange={e => { setFilters(f => ({ ...f, date_to: e.target.value })); setPage(1); }} />
        </div>
      </div>

      {/* Stats bar */}
      <div className="flex items-center justify-between mb-3 text-sm text-gray-500">
        <span>{total} applications found</span>
        {totalPages > 1 && (
          <span>Page {page} of {totalPages}</span>
        )}
      </div>

      {loading ? (
        <LoadingSpinner size="lg" className="py-20" />
      ) : filtered.length === 0 ? (
        <div className="card text-center py-16">
          <p className="text-5xl mb-3">🔍</p>
          <p className="text-lg font-semibold text-gray-700">No applications found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(app => {
            const step = STATUS_STEP[app.status] ?? 1;
            return (
              <div key={app.id} className="card hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-4 flex-wrap">

                  {/* App info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-xs text-blue-600 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded font-semibold">
                        {app.application_no}
                      </span>
                      <StatusBadge status={app.status} />
                    </div>
                    <p className="font-semibold text-sm text-gray-800">{app.noc_name}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                      <span>👤 {app.user_name}</span>
                      <span>📱 {app.mobile}</span>
                      <span>📅 {app.submitted_at ? format(new Date(app.submitted_at), 'dd MMM yyyy') : '—'}</span>
                    </div>
                  </div>

                  {/* Flow steps */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {/* Step 1: Citizen */}
                    <div className={`flex flex-col items-center px-3 py-2 rounded-xl border text-xs font-medium
                      bg-blue-50 border-blue-200 text-blue-700`}>
                      <span className="text-base mb-0.5">👤</span>
                      <span>Citizen</span>
                      <span className="text-green-600 text-xs">✓</span>
                    </div>

                    <span className="text-gray-300 text-lg">→</span>

                    {/* Step 2: Related Dept */}
                    <div className={`flex flex-col items-center px-3 py-2 rounded-xl border text-xs font-medium
                      ${step >= 1
                        ? step === 1
                          ? 'bg-orange-50 border-orange-300 text-orange-700 ring-2 ring-orange-300'
                          : 'bg-green-50 border-green-200 text-green-700'
                        : 'bg-gray-50 border-gray-200 text-gray-400'}`}>
                      <span className="text-base mb-0.5">🏢</span>
                      <span className="whitespace-nowrap">Related Dept</span>
                      <span className="text-xs">
                        {step > 1 ? '✓' : step === 1 ? '⏳' : '—'}
                      </span>
                    </div>

                    <span className="text-gray-300 text-lg">→</span>

                    {/* Step 3: Final Dept */}
                    <div className={`flex flex-col items-center px-3 py-2 rounded-xl border text-xs font-medium
                      ${step >= 2
                        ? step === 2
                          ? 'bg-orange-50 border-orange-300 text-orange-700 ring-2 ring-orange-300'
                          : 'bg-green-50 border-green-200 text-green-700'
                        : 'bg-gray-50 border-gray-200 text-gray-400'}`}>
                      <span className="text-base mb-0.5">🏛️</span>
                      <span className="whitespace-nowrap">Final Dept</span>
                      <span className="text-xs">
                        {step > 2 ? '✓' : step === 2 ? '⏳' : '—'}
                      </span>
                    </div>

                    <span className="text-gray-300 text-lg">→</span>

                    {/* Step 4: Result */}
                    <div className={`flex flex-col items-center px-3 py-2 rounded-xl border text-xs font-medium
                      ${app.status === 'approved_final'
                        ? 'bg-green-100 border-green-400 text-green-800 ring-2 ring-green-300'
                        : app.status === 'rejected_final' || app.status === 'rejected_by_related'
                          ? 'bg-red-100 border-red-400 text-red-800'
                          : app.status === 'pulled'
                            ? 'bg-gray-100 border-gray-300 text-gray-500'
                            : 'bg-gray-50 border-gray-200 text-gray-400'}`}>
                      <span className="text-base mb-0.5">
                        {app.status === 'approved_final' ? '🏆'
                          : app.status?.includes('rejected') ? '❌'
                          : app.status === 'pulled' ? '🚫' : '⬜'}
                      </span>
                      <span>
                        {app.status === 'approved_final' ? 'Approved'
                          : app.status?.includes('rejected') ? 'Rejected'
                          : app.status === 'pulled' ? 'Pulled'
                          : 'Pending'}
                      </span>
                    </div>
                  </div>

                  {/* Currently at + View */}
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-xs text-gray-400">Currently at:</p>
                      <p className="text-sm font-semibold text-gray-700">
                        {app.status === 'approved_final' || app.status?.includes('rejected') || app.status === 'pulled'
                          ? '✅ Completed'
                          : app.current_dept || 'Processing'}
                      </p>
                    </div>
                    <Link to={`/admin/applications/${app.id}`}
                      className="text-xs bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 px-3 py-1.5 rounded-lg font-medium transition-all">
                      View Details →
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="px-4 py-2 text-sm border border-gray-200 rounded-xl disabled:opacity-40 hover:bg-gray-50">
            ← Prev
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const p = page <= 3 ? i + 1 : page - 2 + i;
            if (p < 1 || p > totalPages) return null;
            return (
              <button key={p} onClick={() => setPage(p)}
                className={`px-4 py-2 text-sm border rounded-xl transition-all
                  ${p === page ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold' : 'border-gray-200 hover:bg-gray-50'}`}>
                {p}
              </button>
            );
          })}
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            className="px-4 py-2 text-sm border border-gray-200 rounded-xl disabled:opacity-40 hover:bg-gray-50">
            Next →
          </button>
        </div>
      )}
    </div>
  );
}